<!-- The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work. -->

<template>
  <div class="min-h-screen bg-gray-50 p-6">
    <v-card outlined class="mx-auto my-4">
      <!-- Header -->
      <v-card class="mb-4" flat>
        <v-card-title class="justify-space-between">
          <span class="text-h6 font-weight-bold">{{ $t("examAnalytics.validity") }}</span>
          <v-btn-toggle>
            <v-btn icon small color="primary" @click="toggleHelpModal">
              <v-icon small>mdi-help-circle</v-icon>
            </v-btn>
          </v-btn-toggle>
        </v-card-title>
      </v-card>

      <!-- Summary -->
      <v-row class="mb-4 mx-1">
        <v-col cols="12" md="3">
          <v-card outlined class="pa-4">
            <div class="caption mb-1">{{ $t("examAnalytics.Average DP") }}</div>
            <div class="d-flex justify-space-between align-center">
              <div class="text-h5 font-weight-bold">{{ summaryStats.avgDP.toFixed(2) }}</div>
              <v-chip small :color="getDPColor(summaryStats.avgDP)" dark>{{
                getDPVerdict(summaryStats.avgDP)
              }}</v-chip>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined class="pa-4">
            <div class="caption mb-1">{{ $t("examAnalytics.Average DE") }}</div>
            <div class="d-flex justify-space-between align-center">
              <div class="text-h5 font-weight-bold">
                {{ (summaryStats.avgDE * 100).toFixed(0) }}%
              </div>
              <v-chip small :color="getDEColor(summaryStats.avgDE)" dark>{{
                getDEVerdict(summaryStats.avgDE)
              }}</v-chip>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined class="pa-4">
            <div class="caption mb-1">{{ $t("examAnalytics.Questions with Strong VI") }}</div>
            <div class="d-flex justify-space-between align-center">
              <div class="text-h5 font-weight-bold">{{ summaryStats.strongVICount }}</div>
              <div class="text-caption">
                {{ $t("examAnalytics.of") }} {{ questionStats.length }}
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined class="pa-4">
            <div class="caption mb-1">{{ $t("examAnalytics.Overall Verdict") }}</div>
            <div class="d-flex justify-center">
              <v-chip small :color="getOverallVerdictColor()" dark>{{
                getOverallVerdict()
              }}</v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-card outlined class="mx-3 my-3">
        <v-card-text>
          <v-row align="center" justify="space-between">
            <v-col cols="6" md="3">
              <div class="d-flex">
                <v-select
                  v-model="sortBy"
                  :items="[
                    { text: $t('examAnalytics.Question Number'), value: 'number' },
                    { text: $t('examAnalytics.Discrimination Power'), value: 'dp' },
                    { text: $t('examAnalytics.Distractor Efficiency'), value: 'de' },
                    { text: $t('examAnalytics.Validity Index'), value: 'vi' }
                  ]"
                  :label="$t('sortby')"
                  dense
                  outlined
                  hide-details
                ></v-select>
                <v-btn
                  class="mx-3"
                  depressed
                  fab
                  small
                  @click="sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'"
                >
                  <v-icon>{{ sortDirection === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-simple-table dense class="mt-4">
            <thead>
              <tr>
                <th>{{ $t("examAnalytics.Question") }}</th>

                <th>
                  <div class="d-flex align-center">
                    {{ $t("examAnalytics.Discrimination Power (DP)") }}
                    <v-tooltip top content-class="custom-tooltip-width">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="mx-1" color="primary" v-bind="attrs" v-on="on">
                          mdi-information
                        </v-icon>
                      </template>
                      <span>
                        {{ $t("examAnalytics.DiscriminationPowerHint") }}
                      </span>
                    </v-tooltip>
                  </div>
                </th>

                <th>
                  <div class="d-flex align-center">
                    {{ $t("examAnalytics.Distractor Efficiency (DE)") }}
                    <v-tooltip top content-class="custom-tooltip-width">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="mx-1" color="primary" v-bind="attrs" v-on="on">
                          mdi-information
                        </v-icon>
                      </template>
                      <span>
                        {{ $t("examAnalytics.DistractorEfficiencyHint") }}
                      </span>
                    </v-tooltip>
                  </div>
                </th>

                <th>
                  <div class="d-flex align-center">
                    {{ $t("examAnalytics.Validity Index (VI)") }}
                    <v-tooltip top content-class="custom-tooltip-width">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="mx-1" color="primary" v-bind="attrs" v-on="on">
                          mdi-information
                        </v-icon>
                      </template>
                      <span>
                        {{ $t("examAnalytics.ValidityIndexHint") }}
                      </span>
                    </v-tooltip>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="question in paginatedQuestions" :key="question.number">
                <td :title="question.questionTitle">
                  {{ $t("examAnalytics.Q") }} {{ question.number }}
                </td>

                <td>
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span>{{ formatSmartDecimal(question.dp) }}</span>
                    <v-chip x-small :color="getDPColor(question.dp)" dark>{{
                      getDPVerdict(question.dp)
                    }}</v-chip>
                  </div>
                  <v-progress-linear
                    :value="question.dp * 100"
                    height="8"
                    :color="getDPColor(question.dp) + ' darken-1'"
                    :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-3'"
                    class="mb-2 rounded-pill"
                    rounded
                  ></v-progress-linear>
                </td>

                <td>
                  <div class="d-flex align-center">
                    <span>{{ (question.de * 100).toFixed(0) }}%</span>
                    <v-chip class="mx-2" x-small :color="getDEColor(question.de)" dark>{{
                      getDEVerdict(question.de)
                    }}</v-chip>
                  </div>
                </td>

                <td>
                  <div class="d-flex align-center">
                    <span>{{ formatSmartDecimal(question.vi) }}</span>
                    <v-chip class="mx-2" x-small :color="getVIColor(question.vi)" dark>{{
                      getVIVerdict(question.vi)
                    }}</v-chip>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <v-divider></v-divider>
          <v-row align="center" justify="space-between" class="ma-2">
            <!-- Student Count -->
            <v-col cols="12" md="4" class="text-start">
              <span class="grey--text font-weight-medium"
                >{{ filteredAndSortedQuestions().length }} {{ $t("examAnalytics.Question") }}</span
              >
            </v-col>

            <!-- Pagination Control -->
            <v-col cols="12" md="4" class="text-center">
              <v-pagination v-model="page" :length="pageCount" :total-visible="15" color="primary" />
            </v-col>

            <!-- Items Per Page -->
            <v-col cols="12" md="4" class="text-end">
              <v-select
                v-model="itemsPerPage"
                :items="[5, 10, 15, 25]"
                :label="$t('rowsPerPage')"
                dense
                outlined
                hide-details
                style="max-width: 160px; display: inline-block"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Table -->
    </v-card>

    <!-- Help Modal -->
    <v-dialog v-model="showHelpModal" max-width="700">
      <v-card>
        <v-card-title class="headline justify-space-between">
          {{ $t("examAnalytics.Understanding Internal Validity Metrics") }}
          <v-btn icon @click="showHelpModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row dense>
              <v-col cols="12" class="mb-4">
                <h3 class="subtitle-1 font-weight-bold">
                  {{ $t("examAnalytics.Discrimination Power (DP)") }}
                </h3>
                <p>
                  {{
                    $t(
                      "examAnalytics.Measures how well a question distinguishes between high and low performers"
                    )
                  }}.
                </p>
                <ul>
                  <li>
                    <span class="green--text font-weight-medium">{{
                      $t("examAnalytics.Excellent")
                    }}</span
                    >: ≥ 0.3
                  </li>
                  <li>
                    <span class="orange--text font-weight-medium">{{
                      $t("examAnalytics.Acceptable")
                    }}</span
                    >: 0.1 – 0.3
                  </li>
                  <li>
                    <span class="red--text font-weight-medium">{{ $t("examAnalytics.Poor") }}</span
                    >: &lt; 0.1
                  </li>
                </ul>
              </v-col>

              <v-col cols="12" class="mb-4">
                <h3 class="subtitle-1 font-weight-bold">
                  {{ $t("examAnalytics.Distractor Efficiency (DE)") }}
                </h3>
                <p>
                  {{
                    $t(
                      "examAnalytics.The proportion of incorrect options selected more often by low scorers"
                    )
                  }}.
                </p>
                <ul>
                  <li>
                    <span class="green--text font-weight-medium">{{
                      $t("examAnalytics.Good")
                    }}</span
                    >: ≥ 50%
                  </li>
                  <li>
                    <span class="orange--text font-weight-medium">{{
                      $t("examAnalytics.Low")
                    }}</span
                    >: &lt; 50%
                  </li>
                </ul>
              </v-col>

              <v-col cols="12" class="mb-4">
                <h3 class="subtitle-1 font-weight-bold">
                  {{ $t("examAnalytics.Validity Index (VI)") }}
                </h3>
                <p>
                  {{
                    $t(
                      "examAnalytics.Calculated as DP x DE, Provides a combined quality score per question"
                    )
                  }}.
                </p>
                <ul>
                  <li>
                    <span class="green--text font-weight-medium">{{
                      $t("examAnalytics.Strong")
                    }}</span
                    >: ≥ 0.3
                  </li>
                  <li>
                    <span class="orange--text font-weight-medium">{{
                      $t("examAnalytics.Moderate")
                    }}</span
                    >: 0.1 – 0.3
                  </li>
                  <li>
                    <span class="red--text font-weight-medium">{{ $t("examAnalytics.Weak") }}</span
                    >: &lt; 0.1
                  </li>
                </ul>
              </v-col>

              <v-col cols="12">
                <h3 class="subtitle-1 font-weight-bold">
                  {{ $t("examAnalytics.Overall Verdict") }}
                </h3>
                <p>
                  {{ $t("examAnalytics.Based on the percentage of questions with strong VI") }}:
                </p>
                <ul>
                  <li>
                    <span class="green--text font-weight-medium">{{
                      $t("examAnalytics.Strong")
                    }}</span
                    >: ≥ 70% {{ $t("examAnalytics.strong VI") }}
                  </li>
                  <li>
                    <span class="orange--text font-weight-medium">{{
                      $t("examAnalytics.Moderate")
                    }}</span
                    >: 50%–69%
                  </li>
                  <li>
                    <span class="red--text font-weight-medium">{{
                      $t("examAnalytics.Needs Review")
                    }}</span
                    >: &lt; 50%
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "InternalValidity",
  props: {
    ExamStatisticsData: {
      type: Object,
      required: true
    },
    Questions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      page: 1,
      itemsPerPage: 10,
      questionStats: [],
      searchQuery: "",
      sortBy: "number",
      sortDirection: "asc",
      showHelpModal: false,
      activeTooltip: null,
      tooltipStyle: {
        top: "0px",
        left: "0px"
      }
    };
  },
  computed: {
    summaryStats() {
      const totalQuestions = this.questionStats.length;
      const totalDP = this.questionStats.reduce((sum, q) => sum + q.dp, 0);
      const totalDE = this.questionStats.reduce((sum, q) => sum + q.de, 0);
      const strongVICount = this.questionStats.filter(q => q.vi >= 0.3).length;

      return {
        avgDP: totalDP / totalQuestions,
        avgDE: totalDE / totalQuestions,
        strongVICount
      };
    },

    pageCount() {
      return Math.ceil(this.filteredAndSortedQuestions().length / this.itemsPerPage);
    },
    paginatedQuestions() {
      const start = (this.page - 1) * this.itemsPerPage;
      return this.filteredAndSortedQuestions().slice(start, start + this.itemsPerPage);
    }
  },
  created() {
    this.questionStats = this.ExamStatisticsData.questions.map((question, index) => ({
      number: index + 1,
      questionTitle: this.Questions.filter(m => m.questionGuid == question.questionGuid)[0]
        .questionTitle,
      dp: question.dp,
      de: question.de,
      vi: question.vi
    }));
  },
  methods: {
    filteredAndSortedQuestions() {
      let result = [...this.questionStats];

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(
          q =>
            `question ${q.number}`.includes(query) ||
            q.dp.toString().includes(query) ||
            q.de.toString().includes(query) ||
            q.vi.toString().includes(query)
        );
      }

      // Sort results
      result.sort((a, b) => {
        let valueA, valueB;

        switch (this.sortBy) {
          case "dp":
            valueA = a.dp;
            valueB = b.dp;
            break;
          case "de":
            valueA = a.de;
            valueB = b.de;
            break;
          case "vi":
            valueA = a.vi;
            valueB = b.vi;
            break;
          default:
            // 'number'
            valueA = a.number;
            valueB = b.number;
        }

        if (this.sortDirection === "asc") {
          return valueA - valueB;
        } else {
          return valueB - valueA;
        }
      });

      return result;
    },
    getDPVerdict(value) {
      if (value < 0.1) return this.$t("examAnalytics.Poor");
      if (value < 0.3) return this.$t("examAnalytics.Acceptable");
      return this.$t("examAnalytics.Excellent");
    },
    getDPBadgeClass(value) {
      if (value < 0.1) return "bg-red-100 text-red-800";
      if (value < 0.3) return "bg-yellow-100 text-yellow-800";
      return "bg-green-100 text-green-800";
    },
    getDPBarColor(value) {
      if (value < 0.1) return "bg-red-500";
      if (value < 0.3) return "bg-yellow-500";
      return "bg-green-500";
    },
    getDEVerdict(value) {
      return value < 0.5 ? this.$t("examAnalytics.Low") : this.$t("examAnalytics.Good");
    },
    getDEBadgeClass(value) {
      return value < 0.5 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800";
    },
    getVIVerdict(value) {
      if (value < 0.1) return this.$t("examAnalytics.Weak");
      if (value < 0.3) return this.$t("examAnalytics.Moderate");
      return this.$t("examAnalytics.Strong");
    },
    getVIBadgeClass(value) {
      if (value < 0.1) return "bg-red-100 text-red-800";
      if (value < 0.3) return "bg-yellow-100 text-yellow-800";
      return "bg-green-100 text-green-800";
    },
    getOverallVerdict() {
      const strongPercentage = (this.summaryStats.strongVICount / this.questionStats.length) * 100;

      if (strongPercentage >= 70) return this.$t("examAnalytics.Strong Internal Validity");
      if (strongPercentage >= 50) return this.$t("examAnalytics.Moderate Internal Validity");
      return this.$t("examAnalytics.Needs Review");
    },
    getOverallVerdictClass() {
      const strongPercentage = (this.summaryStats.strongVICount / this.questionStats.length) * 100;

      if (strongPercentage >= 70) return "bg-green-100 text-green-800";
      if (strongPercentage >= 50) return "bg-yellow-100 text-yellow-800";
      return "bg-red-100 text-red-800";
    },
    toggleHelpModal() {
      this.showHelpModal = !this.showHelpModal;
    },
    showTooltip(type) {
      this.activeTooltip = type;

      // Position the tooltip near the cursor
      this.$nextTick(() => {
        const event = window.event;
        if (event) {
          this.tooltipStyle = {
            top: `${event.clientY + 10}px`,
            left: `${event.clientX + 10}px`
          };
        }
      });

      // Auto-hide tooltip after 3 seconds
      setTimeout(() => {
        if (this.activeTooltip === type) {
          this.activeTooltip = null;
        }
      }, 3000);
    },
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    },
    getDPVerdict(val) {
      return val >= 0.3
        ? this.$t("examAnalytics.Excellent")
        : val >= 0.1
        ? this.$t("examAnalytics.Acceptable")
        : this.$t("examAnalytics.Poor");
    },
    getDEVerdict(val) {
      return val >= 0.5 ? this.$t("examAnalytics.Good") : this.$t("examAnalytics.Low");
    },
    getVIVerdict(val) {
      return val >= 0.3 ? this.$t("examAnalytics.Strong") : this.$t("examAnalytics.Weak");
    },
    getOverallVerdict() {
      const { avgDP, avgDE, strongVICount } = this.summaryStats;
      return avgDP >= 0.25 && avgDE >= 0.5 && strongVICount >= 7
        ? this.$t("examAnalytics.Strong Validity")
        : this.$t("examAnalytics.Needs Review");
    },
    getDPColor(val) {
      if (val >= 0.3) return "green lighten-4 green--text text--darken-3";
      if (val >= 0.1) return "orange lighten-4 orange--text text--darken-3";
      return "red lighten-4 red--text text--darken-3";
    },
    getDEColor(val) {
      return val >= 0.5
        ? "green lighten-4 green--text text--darken-3"
        : "red lighten-4 red--text text--darken-3";
    },
    getVIColor(val) {
      if (val >= 0.3) return "green lighten-4 green--text text--darken-3";
      if (val >= 0.1) return "orange lighten-4 orange--text text--darken-3";
      return "grey lighten-4 grey--text text--darken-2";
    },
    getOverallVerdictColor() {
      const verdict = this.ExamStatisticsData.verdict;
      if (verdict.includes("Strong")) return "green lighten-4 green--text text--darken-3";
      if (verdict.includes("Moderate")) return "orange lighten-4 orange--text text--darken-3";
      return "red lighten-4 red--text text--darken-3";
    },
    exportData() {},
    importData() {}
  }
};
</script>

<style scoped>
/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Remove number input arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.custom-tooltip-width {
  max-width: 200px;
  white-space: normal;
  word-wrap: break-word;
}
</style>
<style>
.rounded-pill .v-progress-linear__determinate {
  border-radius: 100px !important;
}
.v-application .text-h6 {
  font-family: unset !important;
}
.v-application .caption {
  font-family: unset !important;
}
.v-application .headline {
  font-family: unset !important;
}
.v-application .subtitle-1 {
  font-family: unset !important;
}
</style>
